<template>
    <div class="ai-chat">
        <div class="ai-chat-toolbar">
            <button type="button" class="ai-draft-btn ai-draft-btn-ghost" @click="$emit('new-session')">
                <i class="fa fa-arrow-left"></i> New draft
            </button>
            <div class="ai-chat-status">
                <span v-if="session.quote_id">
                    Quote #{{ session.quote_id }}
                </span>
                <span v-else>New session</span>
                <span class="ai-chat-status-dot" :class="'ai-chat-status-' + session.status"></span>
            </div>
            <button
                v-if="session.status === 'active'"
                type="button"
                class="ai-chat-discard"
                title="Discard this draft (you can still see it on the quote's history tab)"
                @click="$emit('discard')"
            >
                <i class="fa fa-trash"></i> Discard
            </button>
        </div>

        <div ref="thread" class="ai-chat-thread">

            <div v-if="renderedMessages.length === 0" class="ai-chat-empty">
                <i class="fa fa-comment-dots"></i>
                <p>This draft has no messages yet.</p>
            </div>

            <message-bubble
                v-for="msg in renderedMessages"
                :key="msg.key"
                :message="msg"
                @action="payload => $emit('action', payload)"
            ></message-bubble>

            <div v-if="thinking" class="ai-chat-thinking">
                <div class="ai-chat-thinking-dots">
                    <span></span><span></span><span></span>
                </div>
                <span class="ai-chat-thinking-text">{{ thinkingLabel }}</span>
            </div>

            <div v-if="error" class="ai-chat-error">
                <i class="fa fa-exclamation-triangle"></i>
                {{ error }}
            </div>

        </div>

        <div class="ai-chat-composer">
            <button
                type="button"
                class="ai-chat-attach"
                :disabled="thinking || uploading"
                :title="uploading ? 'Uploading…' : 'Attach an artwork photo (jpg/png)'"
                @click="$refs.fileInput.click()"
            >
                <i v-if="uploading" class="fa fa-spinner fa-spin"></i>
                <i v-else class="fa fa-paperclip"></i>
            </button>
            <input
                ref="fileInput"
                type="file"
                accept="image/jpeg,image/png"
                style="display:none"
                @change="onFilePicked"
            />
            <textarea
                ref="input"
                v-model="draft"
                class="ai-chat-input"
                rows="1"
                placeholder="Reply, ask a question, or request a tweak…"
                :disabled="thinking"
                @keydown.enter.exact.prevent="submit"
                @keydown.shift.enter.exact="newline"
                @input="autosize"
            ></textarea>
            <button
                type="button"
                class="ai-chat-send"
                :disabled="!canSend"
                :title="thinking ? 'Drafting…' : 'Send (Enter)'"
                @click="submit"
            >
                <i v-if="thinking" class="fa fa-spinner fa-spin"></i>
                <i v-else class="fa fa-arrow-up"></i>
            </button>
        </div>
    </div>
</template>

<script>
import MessageBubble from './MessageBubble.vue';

// Present-tense, single-line action labels for the live "thinking…" indicator.
// Past-tense versions for completed tool-call bubbles live in MessageBubble.vue.
const TOOL_PROGRESS_LABELS = {
    lookup_client: 'Looking up the client',
    get_client_history: 'Pulling client history',
    find_similar_items: 'Searching similar past items',
    get_enquiry: 'Reading the enquiry',
    list_bundles: 'Listing bundles',
    get_bundle: 'Loading a bundle',
    list_segments: 'Listing segments',
    list_product_types: 'Listing product types',
    list_recent_enquiries: 'Listing recent enquiries',
    get_pricing_catalogue: 'Loading the pricing catalogue',
    create_quote_draft: 'Creating the quote',
    update_quote: 'Updating the quote',
    add_item_from_bundle: 'Adding an item',
    add_blank_item: 'Adding a blank item',
    update_item_options: 'Updating item options',
    set_item_dimensions: 'Setting dimensions',
    recalculate_quote: 'Recalculating the quote',
    get_quote_total: 'Checking the quote total',
    get_item_price_breakdown: 'Looking at the price breakdown',
    suggest_cost_reductions: 'Looking for cost reductions',
    apply_alternative: 'Applying an alternative',
    compare_alternatives: 'Comparing alternatives',
    summarise_quote: 'Summarising the draft',
    parse_enquiry_email: 'Parsing the enquiry email',
    classify_client_status: 'Classifying the client',
    generate_item_description: 'Generating an item description',
    validate_dimensions: 'Validating dimensions',
    copy_quote: 'Copying the existing quote',
    split_tiered_quote: 'Splitting the tiered quote',
};

export default {
    components: { MessageBubble },

    props: {
        session: { type: Object, required: true },
        messages: { type: Array, required: true },
        thinking: { type: Boolean, default: false },
        error: { type: String, default: null },
    },

    data: () => ({ draft: '', uploading: false }),

    computed: {
        // Pair tool_call with its matching tool_result (by tool_use_id) so the
        // bubble can render both sides as one collapsible line. user/assistant
        // messages pass through unchanged.
        renderedMessages() {
            const byUseId = {};
            this.messages.forEach(m => {
                if (m.role === 'tool_result' && m.tool_use_id) {
                    byUseId[m.tool_use_id] = m;
                }
            });

            const out = [];
            this.messages.forEach(m => {
                if (m.role === 'tool_result') return; // rendered alongside its tool_call
                if (m.role === 'tool_call') {
                    const result = m.tool_use_id ? byUseId[m.tool_use_id] : null;
                    out.push({
                        key: 'tc-' + m.id,
                        kind: 'tool',
                        tool_name: m.tool_name,
                        tool_params: m.tool_params,
                        tool_result: result ? result.tool_result : null,
                        seq: m.seq,
                    });
                    return;
                }
                if (m.role === 'assistant') {
                    out.push({
                        key: 'a-' + m.id,
                        kind: 'assistant',
                        content: m.content,
                        seq: m.seq,
                    });
                    return;
                }
                if (m.role === 'user') {
                    out.push({
                        key: 'u-' + m.id,
                        kind: 'user',
                        content: m.content,
                        seq: m.seq,
                    });
                }
            });

            // Attach a summary card to the LAST assistant message that follows a
            // SUCCESSFUL summarise_quote tool call. Errored summaries are shown
            // only via the collapsible tool line.
            const lastSummary = [...this.messages].reverse().find(m =>
                m.role === 'tool_call' && m.tool_name === 'summarise_quote'
            );
            if (lastSummary) {
                const result = lastSummary.tool_use_id ? byUseId[lastSummary.tool_use_id] : null;
                const summaryPayload = result && result.tool_result;
                const errored = summaryPayload && typeof summaryPayload === 'object' && summaryPayload.error;
                if (summaryPayload && !errored) {
                    const lastAssistant = [...out].reverse().find(m => m.kind === 'assistant');
                    if (lastAssistant) {
                        lastAssistant.summary = summaryPayload;
                        lastAssistant.summaryQuoteId = this.session.quote_id;
                    }
                }
            }

            return out;
        },

        canSend() {
            return !this.thinking && this.draft.trim().length > 0;
        },

        // Show the most recent tool Claude requested (or "Drafting…" if it's
        // still composing the first turn). Driven by the live-progress poll
        // which surfaces tool_calls as they're persisted.
        thinkingLabel() {
            if (!this.thinking) return '';
            const lastToolCall = [...this.messages].reverse().find(m =>
                m.role === 'tool_call' && m.tool_name
            );
            if (!lastToolCall) return 'Drafting…';
            const label = TOOL_PROGRESS_LABELS[lastToolCall.tool_name] || lastToolCall.tool_name;
            return label + '…';
        },
    },

    watch: {
        messages() {
            this.$nextTick(this.scrollToBottom);
        },
        thinking(val) {
            if (!val) {
                this.$nextTick(() => {
                    this.$refs.input && this.$refs.input.focus();
                });
            } else {
                this.$nextTick(this.scrollToBottom);
            }
        },
    },

    mounted() {
        this.$nextTick(() => {
            this.scrollToBottom();
            this.$refs.input && this.$refs.input.focus();
        });
    },

    methods: {
        onFilePicked(e) {
            const file = e.target.files && e.target.files[0];
            e.target.value = ''; // allow re-picking the same file
            if (!file) return;
            this.uploading = true;
            // Parent owns the API + message list; resolve/reject drives the spinner.
            this.$emit('upload-image', file, () => {
                this.uploading = false;
            });
        },

        submit() {
            const t = this.draft.trim();
            if (!t || this.thinking) return;
            this.$emit('send', t);
            this.draft = '';
            this.$nextTick(() => this.autosize());
        },

        newline() {
            // shift+enter handled by browser; placeholder so the modifier guard works.
        },

        autosize() {
            const el = this.$refs.input;
            if (!el) return;
            el.style.height = 'auto';
            el.style.height = Math.min(el.scrollHeight, 200) + 'px';
        },

        scrollToBottom() {
            const el = this.$refs.thread;
            if (!el) return;
            el.scrollTop = el.scrollHeight;
        },
    },
};
</script>

<style>
.ai-chat {
    display: flex;
    flex-direction: column;
    background: var(--ai-card);
    border: 1px solid var(--ai-border);
    border-radius: var(--ai-radius);
    box-shadow: var(--ai-shadow);
    overflow: hidden;
    height: calc(100vh - 220px);
    min-height: 480px;
}

.ai-chat-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid var(--ai-border);
    background: #fafbfc;
}

.ai-chat-status {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--ai-muted);
}

.ai-chat-status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--ai-muted);
}

.ai-chat-status-active {
    background: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, .15);
}

.ai-chat-status-completed {
    background: #6366f1;
}

.ai-chat-status-abandoned {
    background: #d97706;
}

.ai-chat-discard {
    margin-left: auto;
    background: transparent;
    border: 1px solid transparent;
    color: var(--ai-muted);
    border-radius: 6px;
    padding: 4px 10px;
    font: inherit;
    font-size: 12px;
    cursor: pointer;
    transition: background-color .12s, color .12s, border-color .12s;
}

.ai-chat-discard:hover {
    background: #fef2f2;
    color: #dc2626;
    border-color: #fecaca;
}

.ai-chat-thread {
    flex: 1;
    overflow-y: auto;
    padding: 24px 24px 8px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    scroll-behavior: smooth;
}

.ai-chat-empty {
    text-align: center;
    color: var(--ai-muted);
    padding: 60px 20px;
}

.ai-chat-empty .fa {
    font-size: 28px;
    opacity: .5;
    margin-bottom: 10px;
}

.ai-chat-empty p {
    margin: 0;
    font-size: 14px;
}

/* Thinking indicator */

.ai-chat-thinking {
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--ai-muted);
    font-size: 13px;
    padding: 8px 4px;
}

.ai-chat-thinking-dots {
    display: inline-flex;
    gap: 4px;
}

.ai-chat-thinking-dots span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--ai-primary);
    opacity: .4;
    animation: ai-chat-pulse 1.2s ease-in-out infinite;
}

.ai-chat-thinking-dots span:nth-child(2) { animation-delay: .15s; }
.ai-chat-thinking-dots span:nth-child(3) { animation-delay: .3s; }

@keyframes ai-chat-pulse {
    0%, 80%, 100% { opacity: .25; transform: scale(.85); }
    40% { opacity: 1; transform: scale(1.1); }
}

.ai-chat-thinking-text {
    font-style: italic;
}

/* Errors */

.ai-chat-error {
    background: #fef2f2;
    color: #b91c1c;
    border: 1px solid #fecaca;
    border-radius: var(--ai-radius-sm);
    padding: 10px 14px;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 8px;
}

/* Composer */

.ai-chat-composer {
    display: flex;
    gap: 10px;
    align-items: flex-end;
    padding: 12px 16px 14px;
    border-top: 1px solid var(--ai-border);
    background: #fafbfc;
}

.ai-chat-input {
    flex: 1;
    border: 1px solid var(--ai-border);
    border-radius: 22px;
    padding: 10px 18px;
    font: inherit;
    font-size: 14px;
    line-height: 1.5;
    background: #fff;
    resize: none;
    max-height: 200px;
    transition: border-color .15s, box-shadow .15s;
}

.ai-chat-input:focus {
    outline: none;
    border-color: var(--ai-primary);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, .15);
}

.ai-chat-input:disabled {
    background: #f3f4f6;
    cursor: not-allowed;
}

.ai-chat-send {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: var(--ai-primary);
    color: #fff;
    border: none;
    cursor: pointer;
    flex-shrink: 0;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color .12s, transform .05s, opacity .12s;
}

.ai-chat-attach {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: transparent;
    color: var(--ai-primary);
    border: 1px solid var(--ai-border, #d5dbe3);
    cursor: pointer;
    flex-shrink: 0;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color .12s, opacity .12s;
}

.ai-chat-attach:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.05);
}

.ai-chat-attach:disabled {
    opacity: .5;
    cursor: default;
}

.ai-chat-send:hover:not(:disabled) {
    background: var(--ai-primary-hover);
}

.ai-chat-send:active:not(:disabled) {
    transform: translateY(1px);
}

.ai-chat-send:disabled {
    opacity: .4;
    cursor: not-allowed;
}
</style>
